/**
 * The little toolbox over the pet: what the user holds, chosen with the mouse wheel, and
 * how fast a thrown treat leaves the hand. Pure: wheel deltas and pointer samples in.
 */
export type MonkeyTool = "hand" | "peanut" | "banana" | "stick"

export const monkeyTools: readonly { id: MonkeyTool; label: string; hint: string }[] = [
  { id: "hand", label: "Hand", hint: "Sleep om te verplaatsen" },
  { id: "peanut", label: "Nootje", hint: "Klik of gooi een nootje" },
  { id: "banana", label: "Banaan", hint: "Klik om een banaan neer te leggen" },
  { id: "stick", label: "Stok", hint: "Klik op het aapje om te tikken" }
]

/** Within this many pixels of him the wheel picks a tool; further off the mouse is the mouse. */
export const monkeyToolReach = 150

/** Pixels from a point to his on-screen box; zero on him. */
export function distanceToMonkey(
  point: { x: number; y: number },
  bounds: { left: number; top: number; width: number; height: number }
) {
  const dx = Math.max(bounds.left - point.x, 0, point.x - (bounds.left + bounds.width))
  const dy = Math.max(bounds.top - point.y, 0, point.y - (bounds.top + bounds.height))
  return Math.hypot(dx, dy)
}

/** Wheel pixels that make one step through the tools; a mouse notch is about 100. */
const wheelStep = 60
/** A pause this long starts counting afresh. */
const wheelIdle = 300

export function createMonkeyToolWheel() {
  let accumulated = 0
  let lastTime = -Infinity

  /** One step through the tools, -1 or 1, once enough wheel movement has added up. */
  function scroll(deltaPixels: number, time: number): -1 | 0 | 1 {
    if (!Number.isFinite(deltaPixels) || !Number.isFinite(time)) return 0
    if (time - lastTime > wheelIdle || Math.sign(deltaPixels) !== Math.sign(accumulated))
      accumulated = 0
    lastTime = time
    accumulated += deltaPixels
    if (Math.abs(accumulated) < wheelStep) return 0
    const step = accumulated > 0 ? 1 : -1
    accumulated = 0
    return step
  }

  return { scroll }
}

export function nextMonkeyTool(current: MonkeyTool, step: number): MonkeyTool {
  const index = monkeyTools.findIndex((tool) => tool.id === current)
  const count = monkeyTools.length
  return monkeyTools[(((index + Math.trunc(step)) % count) + count) % count].id
}

export interface MonkeyThrowSample {
  x: number
  y: number
  /** Milliseconds, monotonic. */
  time: number
}

/** Only the last stretch of the movement counts for the throw. */
const throwWindow = 100
/** Below this, letting go is putting it down. */
const throwMinimum = 150

/** Pixels per second the hand moved as it let go, or none for a still hand. */
export function monkeyThrowVelocity(samples: readonly MonkeyThrowSample[]) {
  const last = samples.at(-1)
  if (!last) return { x: 0, y: 0 }
  const first = samples.find((sample) => last.time - sample.time <= throwWindow) ?? last
  const elapsed = last.time - first.time
  if (elapsed <= 0) return { x: 0, y: 0 }
  const velocity = {
    x: ((last.x - first.x) * 1000) / elapsed,
    y: ((last.y - first.y) * 1000) / elapsed
  }
  return Math.hypot(velocity.x, velocity.y) < throwMinimum ? { x: 0, y: 0 } : velocity
}
